// Driver pages: jobs board (browse & bid, saved searches) and my bids.
import React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Plus, X, MessageSquare } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { US_STATES } from "@/lib/states";
import { getPushState, enablePush } from "@/lib/push";
import { useUnreadThreads } from "@/hooks/useUnreadThreads";
import { RequestCard, UnreadDot, BidStatusPill, Skeletons, EmptyState } from "./shared";
import { BidThreadModal } from "./BidThreadModal";

// ---------- Driver: Browse Jobs ----------
const stateOf = (city) => {
  const m = (city || "").match(/,\s*([A-Za-z]{2})\b/);
  return m ? m[1].toUpperCase() : null;
};

export function DriverDashboard() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vehicle, setVehicle] = useState("all");
  const [fromState, setFromState] = useState("");
  const [toState, setToState] = useState("");
  const [minBudget, setMinBudget] = useState("");
  const [maxBudget, setMaxBudget] = useState("");
  const [saved, setSaved] = useState([]);
  const [namingSearch, setNamingSearch] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [pushBanner, setPushBanner] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("ph_push_dismissed")) return;
    getPushState().then((s) => setPushBanner(s === "available")).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/requests?status=open");
        if (alive) setRequests(data);
      } catch (err) {
        toast.error(apiErrorMessage(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    api.get("/saved-searches").then((r) => setSaved(r.data)).catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const filtered = requests.filter((r) => {
    if (vehicle !== "all" && r.vehicle_type !== vehicle) return false;
    if (verifiedOnly && !r.shipper_verified) return false;
    if (fromState && stateOf(r.pickup_city) !== fromState) return false;
    if (toState && toState !== "ANY" && stateOf(r.delivery_city) !== toState) return false;
    const lo = minBudget !== "" ? Number(minBudget) : null;
    const hi = maxBudget !== "" ? Number(maxBudget) : null;
    if (lo !== null || hi !== null) {
      if (r.budget_usd == null) return false;
      if (lo !== null && r.budget_usd < lo) return false;
      if (hi !== null && r.budget_usd > hi) return false;
    }
    return true;
  });

  const filtersActive = vehicle !== "all" || !!fromState || !!toState || minBudget !== "" || maxBudget !== "";

  const saveSearch = async () => {
    const name = searchName.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/saved-searches", {
        name,
        filters: {
          vehicle_type: vehicle === "all" ? null : vehicle,
          from_state: fromState || null,
          to_state: toState || null,
          min_budget: minBudget !== "" ? Number(minBudget) : null,
          max_budget: maxBudget !== "" ? Number(maxBudget) : null,
        },
      });
      setSaved((s) => [data, ...s]);
      setNamingSearch(false);
      setSearchName("");
      toast.success(`Saved "${data.name}" — daily digest of new matches at 8am ET.`);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const applySaved = (s) => {
    const f = s.filters || {};
    setVehicle(f.vehicle_type || "all");
    setFromState(f.from_state || "");
    setToState(f.to_state || "");
    setMinBudget(f.min_budget != null ? String(f.min_budget) : "");
    setMaxBudget(f.max_budget != null ? String(f.max_budget) : "");
  };

  const deleteSaved = async (id) => {
    try {
      await api.delete(`/saved-searches/${id}`);
      setSaved((s) => s.filter((x) => x.id !== id));
      toast.success("Saved search deleted.");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const selectCls =
    "bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white px-3 py-2 text-xs uppercase tracking-widest font-bold";
  const inputCls =
    "bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white px-3 py-2 text-xs w-24";

  return (
    <div className="px-6 md:px-10 py-10 md:py-14 max-w-6xl mx-auto" data-testid="driver-dashboard">
      <div className="mb-6">
        <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Open jobs</span>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase mt-2 leading-none">
          Browse & bid
        </h1>
        <p className="text-sm text-zinc-500 mt-3">
          Fresh shipper requests, updated live. 0% commission.
        </p>
      </div>

      {pushBanner && (
        <div className="mb-6 border border-amber-500/40 bg-amber-500/[0.06] p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="push-banner">
          <div className="text-sm text-zinc-300">
            <span className="text-amber-400 font-bold uppercase tracking-widest text-xs block mb-1">Hauls move fast</span>
            Enable phone push — instant matches and counter-offers reach you even with this tab closed.
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                try {
                  await enablePush();
                  localStorage.setItem("ph_push_dismissed", "1");
                  setPushBanner(false);
                  toast.success("Push enabled — you're reachable anywhere.");
                } catch {
                  toast.error("Push blocked or unsupported in this browser.");
                }
              }}
              data-testid="push-banner-enable"
              className="bg-amber-500 hover:bg-amber-400 text-black text-[11px] uppercase tracking-widest font-bold px-4 py-2 transition-colors"
            >
              Enable push
            </button>
            <button
              onClick={() => {
                localStorage.setItem("ph_push_dismissed", "1");
                setPushBanner(false);
              }}
              data-testid="push-banner-dismiss"
              className="text-[11px] uppercase tracking-widest font-bold text-zinc-500 hover:text-white px-3 py-2"
            >
              Not now
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="search-filters"><div className="flex gap-2" data-testid="vehicle-filters">
          {["all", "boat", "rv", "other"].map((v) => (
            <button
              key={v}
              data-testid={`filter-${v}`}
              onClick={() => setVehicle(v)}
              className={`text-[11px] uppercase tracking-widest font-bold px-3 py-2 border transition-colors ${
                vehicle === v
                  ? "border-[#FF3B30] bg-[#FF3B30] text-white"
                  : "border-white/10 text-zinc-400 hover:border-white"
              }`}
            >
              {v}
            </button>
          ))}
        </div>
        <select value={fromState} onChange={(e) => setFromState(e.target.value)} data-testid="filter-from-state" className={selectCls}>
          <option value="">From: Any</option>
          {US_STATES.map((s) => (
            <option key={s.abbr} value={s.abbr}>{s.abbr}</option>
          ))}
        </select>
        <select value={toState} onChange={(e) => setToState(e.target.value)} data-testid="filter-to-state" className={selectCls}>
          <option value="">To: Any</option>
          <option value="ANY">Anywhere</option>
          {US_STATES.map((s) => (
            <option key={s.abbr} value={s.abbr}>{s.abbr}</option>
          ))}
        </select>
        <input
          value={minBudget}
          onChange={(e) => setMinBudget(e.target.value.replace(/[^0-9]/g, ""))}
          placeholder="Min $"
          data-testid="filter-min-budget"
          className={inputCls}
          inputMode="numeric"
        />
        <input
          value={maxBudget}
          onChange={(e) => setMaxBudget(e.target.value.replace(/[^0-9]/g, ""))}
          placeholder="Max $"
          data-testid="filter-max-budget"
          className={inputCls}
          inputMode="numeric"
        />
        <button
          onClick={() => setVerifiedOnly((v) => !v)}
          data-testid="filter-verified-only"
          title="Show only hauls from ID + ownership verified shippers"
          className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold px-3 py-2 border transition-colors ${
            verifiedOnly
              ? "border-emerald-500 bg-emerald-500/15 text-emerald-400"
              : "border-white/10 text-zinc-400 hover:border-white"
          }`}
        >
          <ShieldCheck className="w-3.5 h-3.5" /> Verified shippers
        </button>
        {filtersActive && !namingSearch && (
          <button
            onClick={() => setNamingSearch(true)}
            data-testid="save-search-btn"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold text-[#FF3B30] hover:text-white border border-[#FF3B30] hover:bg-[#FF3B30] px-4 py-2 transition-all"
          >
            <Plus className="w-3.5 h-3.5" /> Save search
          </button>
        )}
        {filtersActive && namingSearch && (
          <span className="inline-flex items-center border border-[#FF3B30] bg-[#0F0F10]">
            <input
              autoFocus
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveSearch()}
              placeholder="Name this search…"
              data-testid="save-search-name-input"
              maxLength={60}
              className="bg-transparent focus:outline-none text-white placeholder-zinc-600 px-3 py-2 text-xs w-44"
            />
            <button
              onClick={saveSearch}
              data-testid="save-search-confirm"
              className="text-[11px] uppercase tracking-widest font-bold text-[#FF3B30] hover:text-white hover:bg-[#FF3B30] px-3 py-2 border-l border-[#FF3B30] transition-colors"
            >
              Save
            </button>
            <button
              onClick={() => { setNamingSearch(false); setSearchName(""); }}
              data-testid="save-search-cancel"
              aria-label="Cancel"
              className="text-zinc-500 hover:text-white px-2.5 py-2 border-l border-white/10 transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        )}
      </div>

      {saved.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="saved-searches">
          <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 font-bold">Saved:</span>
          {saved.map((s) => (
            <span key={s.id} className="inline-flex items-center border border-white/[0.08] bg-[#0F0F10]">
              <button
                onClick={() => applySaved(s)}
                data-testid={`saved-search-chip-${s.id}`}
                className="text-[11px] uppercase tracking-widest font-bold text-zinc-300 hover:text-[#FF3B30] px-3 py-1.5 transition-colors"
              >
                {s.name}
              </button>
              <button
                onClick={() => deleteSaved(s.id)}
                data-testid={`saved-search-delete-${s.id}`}
                aria-label={`Delete ${s.name}`}
                className="text-zinc-600 hover:text-red-400 px-2 py-1.5 border-l border-white/[0.08] transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {loading ? (
        <Skeletons />
      ) : filtered.length === 0 ? (
        <EmptyState
          title={filtersActive ? "Nothing matches those filters" : "No open jobs right now"}
          body={filtersActive ? "Loosen a filter or save the search — new matches land in your daily digest." : "Check back soon — new hauls post daily."}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((r) => (
            <RequestCard key={r.id} r={r} to={`/app/requests/${r.id}`} testid={`open-request-${r.id}`} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Driver: My Bids ----------
export function MyBids() {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/bids/mine");
      setBids(data);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [threadBid, setThreadBid] = useState(null);
  const { by_bid: unreadByBid } = useUnreadThreads();

  const withdraw = async (bidId) => {
    if (!window.confirm("Withdraw this bid? The shipper will no longer see it.")) return;
    try {
      await api.post(`/bids/${bidId}/withdraw`);
      toast.success("Bid withdrawn.");
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  return (
    <div className="px-6 md:px-10 py-10 md:py-14 max-w-4xl mx-auto" data-testid="my-bids-page">
      <div className="mb-8">
        <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Your bids</span>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase mt-2 leading-none">
          My bids
        </h1>
      </div>
      {loading ? (
        <Skeletons />
      ) : bids.length === 0 ? (
        <EmptyState
          title="No bids yet"
          body="Browse the open jobs board and drop your first bid."
          cta={{ to: "/app/driver", label: "Browse jobs" }}
        />
      ) : (
        <div className="space-y-3">
          {bids.map((b) => (
            <div
              key={b.id}
              data-testid={`my-bid-${b.id}`}
              className="border border-white/[0.08] bg-[#0F0F10] p-5 hover:border-[#FF3B30] transition-all"
            >
              <Link to={`/app/requests/${b.request_id}`} className="block">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-display text-lg font-black uppercase tracking-tight">
                    ${b.price_usd.toLocaleString()}
                  </div>
                  <BidStatusPill status={b.status} />
                </div>
                <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{b.message}</p>
                <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-3">
                  Bid submitted {new Date(b.created_at).toLocaleString()}
                </div>
              </Link>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setThreadBid(b)}
                  data-testid={`open-thread-${b.id}`}
                  className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-zinc-300 hover:text-[#FF3B30] border border-white/10 hover:border-[#FF3B30]/60 px-3 py-1.5 transition-all"
                >
                  <MessageSquare className="w-3.5 h-3.5" /> Negotiate
                  <UnreadDot count={unreadByBid[b.id]} testid={`unread-badge-${b.id}`} />
                </button>
                {b.status === "pending" && (
                  <button
                    onClick={() => withdraw(b.id)}
                    data-testid={`withdraw-bid-${b.id}`}
                    className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-zinc-400 hover:text-[#FF3B30] border border-white/10 hover:border-[#FF3B30]/60 px-3 py-1.5 transition-all"
                  >
                    <X className="w-3 h-3" /> Withdraw bid
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {threadBid && (
        <BidThreadModal
          bid={threadBid}
          onClose={() => setThreadBid(null)}
          onChange={load}
        />
      )}
    </div>
  );
}

